import { useState, useMemo, useEffect } from 'react';
import { useApp } from '../context/AppContext';
import CarryOverIncidents from '../components/CarryOverIncidents';
import { Package, Truck, Save, QrCode, MessageSquare, UserCheck, Send, RotateCcw, CalendarDays } from 'lucide-react';

const MONTHS_ABBR = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const MONTH_TO_NUM = { Jan:'01',Feb:'02',Mar:'03',Apr:'04',May:'05',Jun:'06',Jul:'07',Aug:'08',Sep:'09',Oct:'10',Nov:'11',Dec:'12' };
const ROUTES = ['Darlinghurst', 'Broadway', 'City'];
const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

// Source of truth from DoE QR Codes page — login → collection point(s)
const LOGIN_TO_SCHOOLS = {
  FERNANDEB3:  ['Fort Street Public School'],
  SOTOASTOC:   ['Conservatorium'],
  RONAS:       ['Observatory Hill'],
  BAGNATOA:    ['Bridge Road School', 'RPA Public School'],
  SHARLANDS2:  ['Forest Lodge Public School'],
  GARCIARIP:   ['Glebe Public School'],
  TUMURO:      ['Syd Sec Coll Blackwattle Bay'],
  MEDINACOK:   ['Ultimo Public'],
  GALLEGUIL:   ['Bourke St Public School'],
  MEDINAHM2:   ['Bourke St Public School'],
  MEDINAHEM2:  ['Crown St Public School'],
  KIMJ8:       ['Bourke St Public School', 'Darlinghurst Public School', 'Plunket St Public School', 'Syd Distance Ed', '(TBA)'],
  NGUYENHAT:   ['Plunket St Public School'],
  ZHOUM5:      ['Syd Distance Ed'],
  CAVIERESD:   ['Inner Sydney School'],
  CAVIERESD2:  ['Inner Sydney School'],
};

// Drivers that always appear on the DoE list regardless of historical data
const DOE_PERMANENT_LOGINS = ['SHARLANDS2', 'CAVIERESD2'];

// Old login → new login renames (sheet history may still use old names)
const LOGIN_RENAMES = { CAVIERESD: 'CAVIERESD2' };

// NSW School Terms — DoE collections only run during term
// Each entry: [termStartISO, termEndISO]
const NSW_SCHOOL_TERMS = [
  // 2025
  ['2025-01-29', '2025-04-11'],
  ['2025-04-28', '2025-07-04'],
  ['2025-07-21', '2025-09-26'],
  ['2025-10-13', '2025-12-19'],
  // 2026
  ['2026-01-28', '2026-04-09'],
  ['2026-04-27', '2026-07-03'],
  ['2026-07-20', '2026-09-25'],
  ['2026-10-12', '2026-12-18'],
  // 2027
  ['2027-01-27', '2027-04-01'],
  ['2027-04-19', '2027-07-02'],
  ['2027-07-19', '2027-09-24'],
  ['2027-10-11', '2027-12-17'],
];

function isNSWSchoolTerm(isoDate) {
  if (!isoDate) return true;
  return NSW_SCHOOL_TERMS.some(([start, end]) => isoDate >= start && isoDate <= end);
}

// Inner Sydney School collects Mon + Thu; all others collect Tue + Thu
// Blocked days: 0=Sun, 3=Wed, 5=Fri, 6=Sat
const INNER_SYDNEY_SCHOOL = 'Inner Sydney School';
const INNER_SYDNEY_LOGIN = 'CAVIERESD2'; // fallback before live assignments load
function getAllowedDayState(isoDate) {
  if (!isoDate) return 'allowed';
  if (!isNSWSchoolTerm(isoDate)) return 'holidays';
  const day = new Date(isoDate + 'T00:00:00').getDay();
  if (day === 2 || day === 4) return 'allowed';         // Tue / Thu — all drivers
  if (day === 1) return 'monday';                        // Mon — Inner Sydney only
  return 'blocked';                                      // Sun / Wed / Fri / Sat
}
// innerSydneyLogins: Set of logins currently assigned to Inner Sydney School
// (the Mon+Thu rule follows the school, so it survives a permanent swap).
function isLoginAllowedOnDay(login, dayState, innerSydneyLogins) {
  if (dayState === 'allowed') return true;
  if (dayState === 'monday') {
    const up = login.toUpperCase();
    return innerSydneyLogins ? innerSydneyLogins.has(up) : up === INNER_SYDNEY_LOGIN;
  }
  return false;
}

const COLLECTION_POINTS = [
  { qr: 'SPB000000553865', school: 'Fort Street Public School',      drivers: ['FERNANDEB3'] },
  { qr: 'SPB000000600323', school: 'Conservatorium',                 drivers: ['SOTOASTOC'] },
  { qr: 'SPB000000600316', school: 'Observatory Hill',               drivers: ['RONAS'] },
  { qr: 'SPB000000600460', school: 'Bridge Road School',             drivers: ['BAGNATOA'] },
  { qr: 'SPB000000600613', school: 'Forest Lodge Public School',     drivers: ['SHARLANDS2'] },
  { qr: 'SPB000000600644', school: 'Glebe Public School',            drivers: ['GARCIARIP'] },
  { qr: 'SPB000000600408', school: 'RPA Public School',              drivers: ['BAGNATOA'] },
  { qr: 'SPB000000600545', school: 'Syd Sec Coll Blackwattle Bay',  drivers: ['TUMURO'] },
  { qr: 'SPB000000600606', school: 'Ultimo Public',                  drivers: ['MEDINACOK'] },
  { qr: 'SPB000000600200', school: 'Bourke St Public School',        drivers: ['GALLEGUIL', 'KIMJ8'] },
  { qr: 'SPB000000600217', school: 'Crown St Public School',         drivers: ['MEDINAHEM2'] },
  { qr: 'SPB000000600194', school: 'Darlinghurst Public School',     drivers: ['KIMJ8'] },
  { qr: 'SPB000000623681', school: 'Plunket St Public School',       drivers: ['KIMJ8', 'NGUYENHAT'] },
  { qr: 'SPB000000553872', school: 'Syd Distance Ed',                drivers: ['KIMJ8', 'ZHOUM5'] },
  { qr: 'SPB000000567718', school: 'Inner Sydney School',            drivers: ['CAVIERESD2'] },
  { qr: 'SPB000000600309', school: '(TBA)',                          drivers: ['KIMJ8'] },
];

function qrImageUrl(code) {
  return `https://api.qrserver.com/v1/create-qr-code/?size=160x160&data=${encodeURIComponent(code)}&format=png&margin=4`;
}

function formatDisplayDate(iso) {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
}

function sheetDateToISO(dateStr) {
  const parts = (dateStr || '').split('-');
  if (parts.length !== 3) return '';
  return `${parts[2]}-${MONTH_TO_NUM[parts[1]] || '00'}-${parts[0].padStart(2, '0')}`;
}

function isoToSheetDate(isoDate) {
  const [year, month, day] = isoDate.split('-').map(Number);
  return `${day}-${MONTHS_ABBR[month - 1]}-${year}`;
}

function reportEndToISO(reportEndDate) {
  return sheetDateToISO(reportEndDate);
}

function addDaysISO(iso, n) {
  const d = new Date(iso + 'T00:00:00');
  d.setDate(d.getDate() + n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export default function DirectEntryPage() {
  const { sheetData, sheetLoading, personnel } = useApp();
  const [activeTab, setActiveTab] = useState('doe');
  const [selectedDate, setSelectedDate] = useState(() => new Date().toLocaleDateString('en-CA', { timeZone: 'Australia/Sydney' })); // Sydney day, not UTC (UTC is yesterday before 10am)
  const [doeInputs, setDoeInputs] = useState({});
  const [depotsInputs, setDepotsInputs] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveResult, setSaveResult] = useState(null);

  const controlsPayPeriodISO = sheetData?.controls?.reportEndDate
    ? reportEndToISO(sheetData.controls.reportEndDate)
    : '';
  const [payPeriodISO, setPayPeriodISO] = useState('');

  // Pre-fill pay period from Controls once data loads, but don't overwrite a manual change
  useEffect(() => {
    if (controlsPayPeriodISO && !payPeriodISO) {
      setPayPeriodISO(controlsPayPeriodISO);
    }
  }, [controlsPayPeriodISO]);

  const dayName  = selectedDate ? DAY_NAMES[new Date(selectedDate + 'T00:00:00').getDay()] : '';
  const dayState = getAllowedDayState(selectedDate);

  const driverRouteMap = sheetData?.driverRouteMap || {};

  // Regular drivers derived from historical sheet data
  const doeRegularDrivers = sheetData?.doeDriverLogins || [];
  const depotsRegularDrivers = sheetData?.depotsDriverLogins || [];

  // Relief drivers added on-the-fly for this session
  const [reliefDoeDrivers, setReliefDoeDrivers] = useState([]);
  const [reliefDepotsDrivers, setReliefDepotsDrivers] = useState([]);
  const [doeReliefInput, setDoeReliefInput] = useState('');
  const [depotsReliefInput, setDepotsReliefInput] = useState('');

  // QR tab state
  // Saved covers (doe_relief runs) from the selected date on — [{school, relief, original, from, to, dates}]
  const [qrCovers, setQrCovers] = useState([]);
  // Per-card draft of a new cover: { [qr]: { driver, period, dates, confirm, busy, result } }
  const [qrCoverDraft, setQrCoverDraft] = useState({});
  const [qrSmsStatus, setQrSmsStatus] = useState({});
  const [qrBulkStatus, setQrBulkStatus] = useState({ monday: null, tuesday: null, thursday: null });

  const phoneByLogin = useMemo(() => {
    const map = {};
    (personnel || []).forEach(p => { if (p.loginName) map[p.loginName.toUpperCase()] = p.phone || ''; });
    return map;
  }, [personnel]);

  // ── Live DoE assignments (DoE_Assignments sheet) ──────────────────────────
  // Hardcoded COLLECTION_POINTS is only the fallback until the fetch lands.
  // The "Permanent driver change" panel below writes to the sheet and updates
  // every consumer (driver portal, reminder email, this page) at once.
  const [livePoints, setLivePoints] = useState(COLLECTION_POINTS);
  useEffect(() => {
    fetch('/.netlify/functions/doe-assign-sb', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'getDoEAssignments' }),
    })
      .then(r => r.json())
      .then(d => { if (d && d.points && d.points.length) setLivePoints(d.points); })
      .catch(() => {});
  }, []);

  const liveLoginToSchools = useMemo(() => {
    const map = {};
    livePoints.forEach(cp => cp.drivers.forEach(d => {
      const l = d.toUpperCase();
      if (!map[l]) map[l] = [];
      if (!map[l].includes(cp.school)) map[l].push(cp.school);
    }));
    return map;
  }, [livePoints]);

  const innerSydneyLogins = useMemo(() => {
    const pt = livePoints.find(cp => cp.school === INNER_SYDNEY_SCHOOL);
    return new Set((pt?.drivers?.length ? pt.drivers : [INNER_SYDNEY_LOGIN]).map(d => d.toUpperCase()));
  }, [livePoints]);

  const qrAllLogins = useMemo(() => {
    const logins = new Set(livePoints.flatMap(cp => cp.drivers));
    (personnel || []).forEach(p => { if (p.loginName) logins.add(p.loginName.toUpperCase()); });
    return [...logins].sort();
  }, [personnel, livePoints]);

  // Who collects this school on the selected date: a saved cover wins, else the usual driver.
  function coverOnDate(cp) { return qrCovers.find(c => c.school === cp.school && c.dates.includes(selectedDate)) || null; }
  function nextCover(cp) { return qrCovers.find(c => c.school === cp.school) || null; }
  function getActiveQrDriver(cp) { const c = coverOnDate(cp); return (c && c.relief) || cp.drivers[0] || ''; }

  const COVER_PERIOD_OPTIONS = [
    ['day', '1 day'], ['1w', '1 week'], ['2w', '2 weeks'], ['3w', '3 weeks'], ['4w', '4 weeks'], ['permanent', 'Permanently'],
  ];
  const shortDay = (iso) => new Date(iso + 'T12:00:00').toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' });

  const assignApi = (body) => fetch('/.netlify/functions/doe-assign-sb', {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
  }).then(r => r.json());

  async function loadQrCovers() {
    try { const d = await assignApi({ action: 'getDoECovers', from: selectedDate }); setQrCovers((d && d.covers) || []); } catch { setQrCovers([]); }
  }
  useEffect(() => { if (activeTab === 'qr' && selectedDate) loadQrCovers(); }, [activeTab, selectedDate]);

  function setDraft(qr, patch) { setQrCoverDraft(d => ({ ...d, [qr]: { ...(d[qr] || { period: '1w' }), ...patch } })); }

  // Re-preview whenever the driver or period changes, so the card says exactly which
  // collection dates the cover holds before anything is saved or texted.
  async function updateDraft(cp, patch) {
    const next = { ...(qrCoverDraft[cp.qr] || { period: '1w' }), ...patch, confirm: false, result: null };
    setDraft(cp.qr, { ...next, dates: null });
    if (!next.driver) return;
    try {
      const d = await assignApi({ action: 'previewDoECover', school: cp.school, from: selectedDate, period: next.period });
      if (d && d.success) setDraft(cp.qr, { dates: d.dates });
    } catch { /* preview is a courtesy — Assign re-checks server-side */ }
  }

  async function assignCover(cp) {
    const dr = qrCoverDraft[cp.qr] || {};
    if (!dr.driver || dr.busy) return;
    if (!dr.confirm) { setDraft(cp.qr, { confirm: true }); return; }
    setDraft(cp.qr, { busy: true, result: null });
    try {
      const d = await assignApi({ action: 'assignDoECover', school: cp.school, qr: cp.qr, relief: dr.driver, original: cp.drivers[0] || '', from: selectedDate, period: dr.period });
      if (!d || !d.success) throw new Error((d && d.error) || 'Assign failed');
      if (d.points && d.points.length) setLivePoints(d.points);
      const smsNote = d.sms === 'sent' ? `HPR text sent to ${dr.driver}.`
        : d.sms === 'no_mobile' ? `No mobile on file for ${dr.driver} - no text sent.`
        : d.sms === 'skipped' ? 'Dates are in the past - no text sent.'
        : `Text to ${dr.driver} failed - use Alert below to retry.`;
      const what = dr.period === 'permanent'
        ? `${dr.driver} is now the usual driver.`
        : `${dr.driver} covers ${d.dates.length} collection${d.dates.length === 1 ? '' : 's'}.`;
      setQrCoverDraft(x => ({ ...x, [cp.qr]: { period: '1w', result: { ok: d.sms === 'sent' || d.sms === 'skipped', msg: `${what} ${smsNote}` } } }));
      await loadQrCovers();
    } catch (err) {
      setDraft(cp.qr, { busy: false, confirm: false, result: { ok: false, msg: err.message } });
    }
  }

  async function endCover(cp, cover) {
    if (!window.confirm(`End ${cover.relief}'s cover of ${cp.school} from ${shortDay(selectedDate)}? Collections from then on go back to ${cp.drivers[0] || 'the usual driver'}. (No text is sent.)`)) return;
    try {
      const d = await assignApi({ action: 'clearDoECover', school: cp.school, from: selectedDate });
      if (!d || !d.success) throw new Error((d && d.error) || 'Could not end cover');
      await loadQrCovers();
    } catch (err) { window.alert(err.message); }
  }

  async function sendQrSMS(cp) {
    const login = getActiveQrDriver(cp);
    const phone = phoneByLogin[login.toUpperCase()];
    if (!phone) {
      setQrSmsStatus(s => ({ ...s, [cp.qr]: { sending: false, result: { ok: false, msg: `No phone on file for ${login}.` } } }));
      return;
    }
    const isRelief = !!coverOnDate(cp);
    const msg = `${dayName} DoE collection reminder: Please scan the QR at ${cp.school} today (${formatDisplayDate(selectedDate)})${isRelief ? ' - covering as relief driver' : ''}. 1 collection. - HollyPaul`;
    setQrSmsStatus(s => ({ ...s, [cp.qr]: { sending: true, result: null } }));
    try {
      const res = await fetch('/.netlify/functions/send-sms', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        // No mediaUrl: the "HPR Engine" sender is alphanumeric, which Twilio can only use for
        // plain SMS — a picture message is rejected with "not a valid ... From phone number".
        body: JSON.stringify({ to: phone, message: msg }),
      });
      const data = await res.json();
      setQrSmsStatus(s => ({ ...s, [cp.qr]: { sending: false, result: { ok: data.ok, msg: data.ok ? `Sent to ${login}${data.skipped ? ' (Twilio not configured)' : ''}` : (data.error || 'Send failed') } } }));
    } catch (err) {
      setQrSmsStatus(s => ({ ...s, [cp.qr]: { sending: false, result: { ok: false, msg: err.message } } }));
    }
  }

  // ── Permanent driver change (writes the DoE_Assignments sheet) ────────────
  const [swapFrom, setSwapFrom]       = useState('');
  const [swapTo, setSwapTo]           = useState('');
  const [swapMode, setSwapMode]       = useState('replace'); // 'replace' | 'remove'
  const [swapConfirm, setSwapConfirm] = useState(false);
  const [swapBusy, setSwapBusy]       = useState(false);
  const [swapResult, setSwapResult]   = useState(null);

  const doeAssignedLogins = useMemo(() =>
    [...new Set(livePoints.flatMap(cp => cp.drivers.map(d => d.toUpperCase())))].sort(),
    [livePoints]);
  const swapAffectedSchools = useMemo(() =>
    swapFrom ? livePoints.filter(cp => cp.drivers.map(d => d.toUpperCase()).includes(swapFrom)).map(cp => cp.school) : [],
    [livePoints, swapFrom]);
  const swapReady = swapFrom && (swapMode === 'remove' || (swapTo.trim() && swapTo.trim().toUpperCase() !== swapFrom));

  async function applyPermanentSwap() {
    if (!swapReady || swapBusy) return;
    if (!swapConfirm) { setSwapConfirm(true); return; }
    setSwapBusy(true); setSwapResult(null);
    try {
      const res = await fetch('/.netlify/functions/doe-assign-sb', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'swapDoEDriver', oldLogin: swapFrom, newLogin: swapMode === 'replace' ? swapTo.trim().toUpperCase() : '' }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Swap failed');
      if (data.points && data.points.length) setLivePoints(data.points);
      setSwapResult({
        ok: true,
        msg: `${swapFrom} ${swapMode === 'replace' ? `replaced by ${swapTo.trim().toUpperCase()}` : 'removed'} at: ${(data.affected || []).join(', ')}. The driver portal, status page and reminder emails now use the new assignment.`,
      });
      setSwapFrom(''); setSwapTo('');
    } catch (err) {
      setSwapResult({ ok: false, msg: err.message });
    } finally {
      setSwapBusy(false); setSwapConfirm(false);
    }
  }

  // Per-school driver list editing (covers a school left with no driver).
  const [schoolEdit, setSchoolEdit]         = useState(null); // school name being edited
  const [schoolEditValue, setSchoolEditValue] = useState('');
  const [schoolEditBusy, setSchoolEditBusy] = useState(false);
  const [schoolEditError, setSchoolEditError] = useState('');

  async function saveSchoolDrivers(school) {
    setSchoolEditBusy(true); setSchoolEditError('');
    try {
      const drivers = schoolEditValue.split(',').map(d => d.trim().toUpperCase()).filter(Boolean);
      const res = await fetch('/.netlify/functions/doe-assign-sb', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'setDoEAssignment', school, drivers }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Save failed');
      if (data.points && data.points.length) setLivePoints(data.points);
      setSchoolEdit(null); setSchoolEditValue('');
    } catch (err) {
      setSchoolEditError(err.message);
    } finally {
      setSchoolEditBusy(false);
    }
  }

  const QR_BULK_DAYS = {
    monday:   { label: 'Alert Monday Providers',   dayLabel: 'Monday',   points: livePoints.filter(cp => cp.school === INNER_SYDNEY_SCHOOL) },
    tuesday:  { label: 'Alert Tuesday Providers',  dayLabel: 'Tuesday',  points: livePoints.filter(cp => cp.school !== INNER_SYDNEY_SCHOOL) },
    thursday: { label: 'Alert Thursday Providers', dayLabel: 'Thursday', points: livePoints },
  };

  async function sendQrBulkAlerts(dayKey) {
    const { points, dayLabel } = QR_BULK_DAYS[dayKey];
    const total = points.length;
    setQrBulkStatus(s => ({ ...s, [dayKey]: { sending: true, sent: 0, failed: 0, total } }));
    let sent = 0, failed = 0;
    for (const cp of points) {
      const login = getActiveQrDriver(cp);
      const phone = phoneByLogin[login.toUpperCase()];
      if (!phone) { failed++; setQrBulkStatus(s => ({ ...s, [dayKey]: { sending: true, sent, failed, total } })); continue; }
      const msg = `${dayLabel} DoE collection reminder: Please scan the QR at ${cp.school} (${formatDisplayDate(selectedDate)}). 1 collection. - HollyPaul`;
      try {
        const res = await fetch('/.netlify/functions/send-sms', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ to: phone, message: msg }),
        });
        const data = await res.json();
        if (data.ok) sent++; else failed++;
      } catch { failed++; }
      setQrBulkStatus(s => ({ ...s, [dayKey]: { sending: true, sent, failed, total } }));
    }
    setQrBulkStatus(s => ({ ...s, [dayKey]: { sending: false, sent, failed, total } }));
  }

  const doeDrivers = useMemo(() => {
    const renamed = doeRegularDrivers.map(l => LOGIN_RENAMES[l] || l);
    return [...new Set([...renamed, ...reliefDoeDrivers, ...DOE_PERMANENT_LOGINS])].sort();
  }, [doeRegularDrivers, reliefDoeDrivers]);

  const depotsDrivers = useMemo(() =>
    [...new Set([...depotsRegularDrivers, ...reliefDepotsDrivers])].sort(),
    [depotsRegularDrivers, reliefDepotsDrivers]
  );

  // ── Depot roster (admin-managed: who sees the Depot Bags task in the portal) ──
  const [depotRoster, setDepotRoster]   = useState(null);   // { LOGIN: { active, route } }
  const [rosterOpen, setRosterOpen]     = useState(false);
  const [rosterSaving, setRosterSaving] = useState(false);
  const [rosterResult, setRosterResult] = useState(null);

  useEffect(() => {
    fetch('/.netlify/functions/depot-roster-sb', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'getDepotRoster' }),
    })
      .then(r => r.json())
      .then(d => {
        const map = {};
        ((d && d.roster) || []).forEach(e => { map[e.login.toUpperCase()] = { active: !!e.active, route: e.route || '' }; });
        setDepotRoster(map);
      })
      .catch(() => setDepotRoster({}));
  }, []);

  // Candidate logins to toggle: everyone in personnel + anyone already on the roster.
  const depotRosterCandidates = useMemo(() => {
    const set = new Set(Object.keys(depotRoster || {}));
    (personnel || []).forEach(p => { if (p.loginName) set.add(p.loginName.toUpperCase()); });
    depotsRegularDrivers.forEach(l => set.add((l || '').toUpperCase()));
    return [...set].filter(Boolean).sort();
  }, [depotRoster, personnel, depotsRegularDrivers]);

  function toggleRosterActive(login) {
    setDepotRoster(m => {
      const cur = m[login] || { active: false, route: driverRouteMap[login] || '' };
      return { ...m, [login]: { ...cur, active: !cur.active } };
    });
    setRosterResult(null);
  }
  function setRosterRoute(login, route) {
    setDepotRoster(m => ({ ...m, [login]: { ...(m[login] || { active: false }), route } }));
    setRosterResult(null);
  }

  async function saveDepotRoster() {
    setRosterSaving(true); setRosterResult(null);
    try {
      const roster = Object.entries(depotRoster || {}).map(([login, v]) => ({ login, active: v.active, route: v.route || '' }));
      const res = await fetch('/.netlify/functions/depot-roster-sb', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'saveDepotRoster', roster }),
      });
      const data = await res.json();
      if (data.success) {
        const activeCount = roster.filter(r => r.active).length;
        setRosterResult({ ok: true, msg: `Saved — ${activeCount} depot driver${activeCount === 1 ? '' : 's'} active.` });
      } else {
        setRosterResult({ ok: false, msg: data.error || 'Save failed.' });
      }
    } catch (err) {
      setRosterResult({ ok: false, msg: err.message || 'Save failed.' });
    } finally {
      setRosterSaving(false);
    }
  }

  function addReliefDriver(type) {
    const raw = type === 'doe' ? doeReliefInput : depotsReliefInput;
    const login = raw.toUpperCase().trim();
    if (!login) return;
    if (type === 'doe') {
      if (!doeDrivers.includes(login)) setReliefDoeDrivers(v => [...v, login]);
      setDoeReliefInput('');
    } else {
      if (!depotsDrivers.includes(login)) setReliefDepotsDrivers(v => [...v, login]);
      setDepotsReliefInput('');
    }
  }

  // ── Status tab: live DoE/Depot completion for the selected date ──────────────
  const [statusLoading, setStatusLoading] = useState(false);
  const [doeByDate, setDoeByDate]         = useState([]);   // [{login, school, quantity, reason}]
  const [depotsByDate, setDepotsByDate]   = useState([]);   // [{login, tally, route}]
  const [reliefByDate, setReliefByDate]   = useState([]);   // [{school, qr, relief, original}]
  const [carryByDate, setCarryByDate]     = useState([]);   // [{driver, name, reason, count, note, photo, submittedAt}]
  const [carryIncidents, setCarryIncidents] = useState([]); // office incident write-ups (missing / stolen parcels)

  useEffect(() => {
    if (activeTab !== 'status' || !selectedDate) return;
    setStatusLoading(true);
    Promise.all([
      fetch('/.netlify/functions/doe-sb', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action: 'getDoEByDate', date: selectedDate }) }).then(r => r.json()).catch(() => ({})),
      fetch('/.netlify/functions/depot-sb', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action: 'getDepotsByDate', date: selectedDate }) }).then(r => r.json()).catch(() => ({})),
      fetch('/.netlify/functions/doe-assign-sb', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action: 'getDoERelief', date: selectedDate }) }).then(r => r.json()).catch(() => ({})),
      fetch('/.netlify/functions/carryover-sb', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action: 'getCarryOverByDate', date: selectedDate }) }).then(r => r.json()).catch(() => ({})),
      fetch('/.netlify/functions/carryover-sb', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action: 'getCarryOverEmailStatus', date: selectedDate }) }).then(r => r.json()).catch(() => ({})),
    ]).then(([doe, dep, rel, carry, mail]) => {
      setDoeByDate((doe && doe.rows) || []);
      setDepotsByDate((dep && dep.rows) || []);
      setReliefByDate((rel && rel.rows) || []);
      setCarryByDate((carry && carry.rows) || []);
      setCarryEmail(mail && mail.success ? mail : null);
    }).finally(() => setStatusLoading(false));
  }, [activeTab, selectedDate]);

  // Depots tab needs the live rows too — the "Already entered today" column drives
  // the one-tally-per-day guard (the old Google-Sheet source went stale when the
  // Depots sheet mirror was dropped).
  useEffect(() => {
    if (activeTab !== 'depots' || !selectedDate) return;
    fetch('/.netlify/functions/depot-sb', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'getDepotsByDate', date: selectedDate }),
    })
      .then(r => r.json())
      .then(dep => setDepotsByDate((dep && dep.rows) || []))
      .catch(() => {});
  }, [activeTab, selectedDate]);

  // ── Period tab: every depot row in the 28-day pay period, as a driver × day grid ──
  const [periodRows, setPeriodRows]       = useState([]);
  const [periodLoading, setPeriodLoading] = useState(false);
  const [periodError, setPeriodError]     = useState('');
  const periodFromISO = payPeriodISO ? addDaysISO(payPeriodISO, -27) : '';

  useEffect(() => {
    if (activeTab !== 'period' || !payPeriodISO) return;
    setPeriodLoading(true); setPeriodError('');
    fetch('/.netlify/functions/depot-sb', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'getDepotsRange', from: periodFromISO, to: payPeriodISO }),
    })
      .then(r => r.json())
      .then(d => {
        if (!d.success) throw new Error(d.error || 'Could not load the period.');
        setPeriodRows(d.rows || []);
      })
      .catch(e => setPeriodError(e.message))
      .finally(() => setPeriodLoading(false));
  }, [activeTab, payPeriodISO]); // eslint-disable-line

  const periodGrid = useMemo(() => {
    const dates = [...new Set(periodRows.map(r => r.date))].sort();
    const drivers = [...new Set(periodRows.map(r => r.login))].sort();
    const cells = {};   // `${login}|${date}` → { sum, count }
    periodRows.forEach(r => {
      const k = `${r.login}|${r.date}`;
      if (!cells[k]) cells[k] = { sum: 0, count: 0 };
      cells[k].sum += r.tally;
      cells[k].count += 1;
    });
    const totals = {};
    drivers.forEach(l => { totals[l] = dates.reduce((s, d) => s + (cells[`${l}|${d}`]?.sum || 0), 0); });
    const grand = drivers.reduce((s, l) => s + totals[l], 0);
    const dupDays = Object.values(cells).filter(c => c.count > 1).length;
    return { dates, drivers, cells, totals, grand, dupDays };
  }, [periodRows]);

  // ── Status tab: amend a logged depot row (admin) ────────────────────────────
  // Depot bags feed RCTI + the driver portal, so a wrong entry (wrong tally, or
  // logged against the wrong driver — e.g. two bags that should have been split
  // across two drivers) needs to be fixable here rather than in a spreadsheet.
  const [depotEdit, setDepotEdit]       = useState(null); // { id, driver, tally, route }
  const [depotRowBusy, setDepotRowBusy] = useState(false);
  const [depotRowMsg, setDepotRowMsg]   = useState('');

  const DEPOT_ROUTE_OPTIONS = ['Broadway', 'Darlinghurst', 'City'];
  const depotDriverOptions = useMemo(() => {
    const set = new Set();
    Object.keys(depotRoster || {}).forEach(l => set.add((l || '').toUpperCase()));
    depotsByDate.forEach(r => set.add((r.login || '').toUpperCase()));
    (depotsDrivers || []).forEach(l => set.add((l || '').toUpperCase()));
    return [...set].filter(Boolean).sort();
  }, [depotRoster, depotsByDate, depotsDrivers]);

  async function reloadDepotsForDate() {
    const dep = await fetch('/.netlify/functions/depot-sb', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'getDepotsByDate', date: selectedDate }),
    }).then(r => r.json()).catch(() => ({}));
    setDepotsByDate((dep && dep.rows) || []);
  }

  async function reloadDoeForDate() {
    const doe = await fetch('/.netlify/functions/doe-sb', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'getDoEByDate', date: selectedDate }),
    }).then(r => r.json()).catch(() => ({}));
    setDoeByDate((doe && doe.rows) || []);
  }

  // Reopen a DoE confirmation: an accidental Collected / Couldn't-collect tap hides
  // the driver's QR + Collected buttons for the day with no in-app undo. Clearing the
  // row here brings them back so the driver can scan again. Keyed by login+school+date.
  const [doeReopenBusy, setDoeReopenBusy] = useState('');   // `${login}|${school}` in flight
  async function reopenDoe(login, school) {
    const key = `${login}|${school}`;
    if (!window.confirm(`Reopen ${login}'s ${school} collection for ${dayName} ${selectedDate}?\n\nThis clears their confirmation so the QR code and Collected button return in their app.`)) return;
    setDoeReopenBusy(key);
    try {
      const res = await fetch('/.netlify/functions/doe-sb', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'deleteDoEConfirmation', driver: login, date: selectedDate, school }),
      });
      const d = await res.json();
      if (d && d.success) await reloadDoeForDate();
      else window.alert(`Couldn't reopen: ${(d && d.error) || 'unknown error'}`);
    } catch (err) { window.alert(`Couldn't reopen: ${err.message}`); }
    setDoeReopenBusy('');
  }

  async function saveDepotEdit() {
    if (!depotEdit) return;
    const t = parseInt(depotEdit.tally, 10);
    if (isNaN(t) || t < 0) { setDepotRowMsg('Tally must be 0 or more.'); return; }
    if (!depotEdit.driver) { setDepotRowMsg('Pick a driver.'); return; }
    setDepotRowBusy(true); setDepotRowMsg('');
    try {
      const res = await fetch('/.netlify/functions/depot-sb', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'updateDepotRow', id: depotEdit.id, driver: depotEdit.driver, tally: t, route: depotEdit.route || '' }),
      }).then(r => r.json());
      if (!res.success) throw new Error(res.error || 'Update failed');
      setDepotEdit(null);
      await reloadDepotsForDate();
    } catch (e) { setDepotRowMsg(e.message || 'Update failed'); }
    finally { setDepotRowBusy(false); }
  }

  async function removeDepotRow(id) {
    setDepotRowBusy(true); setDepotRowMsg('');
    try {
      const res = await fetch('/.netlify/functions/depot-sb', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'deleteDepotRow', id }),
      }).then(r => r.json());
      if (!res.success) throw new Error(res.error || 'Delete failed');
      if (depotEdit && depotEdit.id === id) setDepotEdit(null);
      await reloadDepotsForDate();
    } catch (e) { setDepotRowMsg(e.message || 'Delete failed'); }
    finally { setDepotRowBusy(false); }
  }

  // ── Carry-over email to Australia Post (send from the Status tab) ──────────
  const [carryEmail, setCarryEmail]             = useState(null); // {to, cc, autoSend, sent}
  const [carrySendBusy, setCarrySendBusy]       = useState(false);
  const [carrySendConfirm, setCarrySendConfirm] = useState(false);
  const [carrySendResult, setCarrySendResult]   = useState(null);
  const [carryRowEdit, setCarryRowEdit]         = useState(null); // {driver, reason, value}
  const [carryRowBusy, setCarryRowBusy]         = useState(false);
  const [carryCfgEdit, setCarryCfgEdit]         = useState(null); // {to, cc, autoSend}
  const [carryCfgBusy, setCarryCfgBusy]         = useState(false);

  // Review correction before sending: update one row's parcel count (0 removes).
  async function saveCarryRowCount() {
    const c = parseInt(carryRowEdit.value);
    if (isNaN(c) || c < 0) { setCarryRowEdit(null); return; }
    setCarryRowBusy(true);
    try {
      const res = await fetch('/.netlify/functions/carryover-sb', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'setCarryOverCount', date: selectedDate, driver: carryRowEdit.driver, reason: carryRowEdit.reason, count: c }),
      });
      const data = await res.json();
      if (data.success) { setCarryByDate(data.rows || []); setCarryRowEdit(null); }
    } catch {} finally { setCarryRowBusy(false); }
  }

  async function saveCarryCfg() {
    setCarryCfgBusy(true);
    try {
      const res = await fetch('/.netlify/functions/carryover-sb', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'saveCarryOverConfig', to: carryCfgEdit.to, cc: carryCfgEdit.cc, autoSend: carryCfgEdit.autoSend }),
      });
      const data = await res.json();
      if (data.success) { setCarryEmail(m => ({ ...m, to: data.to, cc: data.cc, autoSend: data.autoSend })); setCarryCfgEdit(null); }
    } catch {} finally { setCarryCfgBusy(false); }
  }

  async function sendCarryOverEmail() {
    if (!carrySendConfirm) { setCarrySendConfirm(true); return; }
    setCarrySendBusy(true); setCarrySendResult(null);
    try {
      const res = await fetch('/.netlify/functions/carryover-email', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ date: selectedDate, by: 'ENGINE' }),
      });
      const data = await res.json();
      if (!data.ok) throw new Error(data.error || 'Send failed');
      setCarrySendResult({ ok: true, msg: `Sent to ${data.to}` });
      setCarryEmail(m => ({ ...m, sent: { sentAt: data.sentAt, by: 'ENGINE', to: data.to } }));
    } catch (err) {
      setCarrySendResult({ ok: false, msg: err.message });
    } finally {
      setCarrySendBusy(false); setCarrySendConfirm(false);
    }
  }

  // Carry-over rows split into actual carry-overs vs "None" compliance answers.
  const carrySplit = useMemo(() => {
    const isNone = r => (r.reason || '').toLowerCase().startsWith('none');
    // A driver's portal "Missing / lost parcel" row is shown as its incident instead, not twice.
    const carried = carryByDate.filter(r => !isNone(r) && !carryIncidents.some(i => i.driver === r.driver && i.type === r.reason));
    // A driver with an incident (e.g. a lost parcel) isn't "all clear" even if they signed off None —
    // same rule the AP email applies.
    const incDrivers = new Set(carryIncidents.map(i => i.driver));
    const clear   = [...new Set(carryByDate.filter(isNone).map(r => r.driver))].filter(d => !incDrivers.has(d)).sort();
    const totalParcels = carried.reduce((s, r) => s + (r.count || 0), 0) + carryIncidents.reduce((s, i) => s + (i.count || 0), 0);
    const byReason = {};
    carried.forEach(r => { byReason[r.reason] = (byReason[r.reason] || 0) + (r.count || 0); });
    carryIncidents.forEach(i => { byReason[i.type] = (byReason[i.type] || 0) + (i.count || 0); });
    return { carried, clear, totalParcels, byReason };
  }, [carryByDate, carryIncidents]);
  const carryNameOf = useMemo(() => {
    const m = {};
    (personnel || []).forEach(p => { if (p.loginName) m[p.loginName.toUpperCase()] = p.name || p.fullName || ''; });
    carryByDate.forEach(r => { if (r.name && r.name !== r.driver) m[r.driver] = r.name; });
    return login => m[login] || '';
  }, [personnel, carryByDate]);
  const carryDrivers = useMemo(() => [...new Set(carryByDate.map(r => r.driver))].sort(), [carryByDate]);

  // Scheduled DoE (effective login + school) for the selected date, relief-aware.
  const scheduledDoE = useMemo(() => {
    if (dayState === 'holidays' || dayState === 'blocked') return [];
    const out = [];
    livePoints.filter(cp => cp.school !== '(TBA)').forEach(cp => {
      const rel = reliefByDate.find(r => r.school === cp.school && r.relief);
      if (rel) { out.push({ login: rel.relief.toUpperCase(), school: cp.school, qr: cp.qr, relief: true }); return; }
      // Monday rule follows the school, not the login.
      const allowed = dayState === 'allowed' || (dayState === 'monday' && cp.school === INNER_SYDNEY_SCHOOL);
      if (!allowed) return;
      cp.drivers.forEach(login => {
        out.push({ login: login.toUpperCase(), school: cp.school, qr: cp.qr, relief: false });
      });
    });
    return out;
  }, [dayState, reliefByDate, livePoints]);

  // Match a scheduled collection to its DoE row (login + school).
  function doeStatusFor(sched) {
    const row = doeByDate.find(r => r.login === sched.login && (r.school || '').trim() === sched.school);
    if (!row) return { state: 'outstanding' };
    if ((row.quantity || 0) > 0) return { state: 'collected' };
    return { state: 'missed', reason: row.reason || '' };
  }

  const existingDoEForDate = useMemo(() => {
    const map = {};
    (sheetData?.doeEntries || []).forEach(e => {
      if (sheetDateToISO(e['Date']) === selectedDate) {
        const raw = (e['Login Name'] || '').toUpperCase();
        const login = LOGIN_RENAMES[raw] || raw;
        map[login] = (map[login] || 0) + (parseInt(e['Quantity'] || '0') || 0);
      }
    });
    return map;
  }, [sheetData, selectedDate]);

  // Live per-driver entries for the selected date (from depot-sb, not the dead sheet mirror).
  const existingDepotsForDate = useMemo(() => {
    const map = {};
    depotsByDate.forEach(r => {
      const login = (r.login || '').toUpperCase();
      if (!map[login]) map[login] = [];
      map[login].push({ tally: r.tally, route: r.route || '' });
    });
    return map;
  }, [depotsByDate]);

  function getDepotsRoute(login) {
    return depotsInputs[login]?.route ?? (driverRouteMap[login] || '');
  }

  async function handleSave(type) {
    setSaving(true);
    setSaveResult(null);
    try {
      const sheetDate = isoToSheetDate(selectedDate);
      let rows = [];
      let action = '';

      if (type === 'doe') {
        action = 'appendDoERows';
        rows = Object.entries(doeInputs)
          .filter(([, qty]) => parseInt(qty) > 0)
          .map(([loginName, qty]) => ({
            date: sheetDate,
            loginName,
            quantity: parseInt(qty),
            payPeriod: payPeriodISO,
          }));
      } else {
        action = 'appendDepotsRows';
        rows = Object.entries(depotsInputs)
          .filter(([, v]) => parseInt(v?.tally) > 0)
          .map(([driverLogin, v]) => ({
            date: sheetDate,
            driverLogin,
            tally: parseInt(v.tally),
            payPeriod: payPeriodISO,
            route: v.route || driverRouteMap[driverLogin] || '',
          }));
      }

      if (!rows.length) {
        setSaveResult({ ok: false, msg: `No ${type === 'doe' ? 'quantities' : 'tallies'} entered — nothing to save.` });
        setSaving(false);
        return;
      }

      // Depots → depot-sb, DoE → doe-sb (both Supabase now).
      const res = await fetch(action === 'appendDepotsRows' ? '/.netlify/functions/depot-sb' : action === 'appendDoERows' ? '/.netlify/functions/doe-sb' : '/.netlify/functions/appsscript', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action, rows }),
      });
      const data = await res.json();
      if (data.success) {
        if (type === 'depots') {
          // One tally per driver per day: the server skips drivers who already have
          // an entry for this date — surface them so they get amended, not lost.
          const skipped = data.skipped || [];
          const saved = data.count ?? rows.length;
          let msg = saved > 0 ? `${saved} row${saved > 1 ? 's' : ''} saved to Depots.` : 'Nothing saved.';
          if (skipped.length) {
            msg += ` Already entered for this day (skipped): ${skipped.map(s => `${s.driver} has ${s.existingTally}`).join(', ')} — amend it in the Status tab.`;
          }
          setSaveResult({ ok: saved > 0, msg });
          setDepotsInputs({});
          reloadDepotsForDate();
        } else {
          setSaveResult({ ok: true, msg: `${rows.length} row${rows.length > 1 ? 's' : ''} saved to DoE.` });
          setDoeInputs({});
        }
      } else {
        throw new Error(data.error || 'Apps Script returned an error — check the Apps Script has the new handlers.');
      }
    } catch (err) {
      setSaveResult({ ok: false, msg: err.message });
    } finally {
      setSaving(false);
    }
  }

  const inputStyle = {
    width: 72,
    textAlign: 'center',
    padding: '4px 6px',
    borderRadius: 'var(--radius)',
    border: '1px solid var(--hp-border)',
    background: 'var(--hp-bg)',
    color: 'var(--hp-text)',
    fontSize: '0.875rem',
  };

  const selectStyle = {
    padding: '4px 8px',
    borderRadius: 'var(--radius)',
    border: '1px solid var(--hp-border)',
    background: 'var(--hp-surface)',
    color: 'var(--hp-text)',
    fontSize: '0.875rem',
  };

  const thStyle = {
    padding: '8px 14px',
    color: 'var(--hp-dim)',
    fontWeight: 600,
    fontSize: '0.75rem',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    borderBottom: '1px solid var(--hp-border)',
    textAlign: 'left',
  };

  const tdStyle = {
    padding: '7px 14px',
    borderBottom: '1px solid var(--hp-border)',
  };

  if (sheetLoading) {
    return (
      <div>
        <div className="page-header">
          <div className="page-header-left">
            <h2>DoE &amp; Depots Entry</h2>
            <p>Loading driver data…</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <div className="page-header-left">
          <h2>DoE &amp; Depots Entry</h2>
          <p>Enter daily delivery counts directly into Google Sheets.</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: '0.8rem', color: 'var(--hp-dim)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Pay period end</span>
          <input
            type="date"
            value={payPeriodISO}
            onChange={e => { setPayPeriodISO(e.target.value); setSaveResult(null); }}
            style={{ padding: '5px 10px', borderRadius: 'var(--radius)', border: '2px solid var(--hp-gold)', background: 'var(--hp-surface)', color: 'var(--hp-gold)', fontWeight: 700, fontSize: '0.875rem' }}
          />
        </div>
      </div>

      <div className="page-body">
        {/* Date selector */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 24 }}>
          <span style={{ fontSize: '0.8rem', color: 'var(--hp-dim)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Entry date</span>
          <input
            type="date"
            value={selectedDate}
            onChange={e => {
              setSelectedDate(e.target.value);
              setSaveResult(null);
              setDoeInputs({});
              setDepotsInputs({});
            }}
            style={{ ...inputStyle, width: 'auto', textAlign: 'left', padding: '5px 10px' }}
          />
          {dayName && (
            <span style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--hp-text)' }}>{dayName}</span>
          )}
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 0, marginBottom: 24, borderBottom: '1px solid var(--hp-border)' }}>
          {[
            { key: 'doe',    label: 'DoE',      icon: <Package size={14} /> },
            { key: 'depots', label: 'Depots',   icon: <Truck size={14} /> },
            { key: 'period', label: 'Period',   icon: <CalendarDays size={14} /> },
            { key: 'status', label: 'Status',   icon: <UserCheck size={14} /> },
            { key: 'qr',     label: 'QR Codes', icon: <QrCode size={14} /> },
          ].map(({ key, label, icon }) => (
            <button
              key={key}
              onClick={() => { setActiveTab(key); setSaveResult(null); }}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '9px 20px',
                border: 'none',
                borderBottom: activeTab === key ? '2px solid var(--hp-gold)' : '2px solid transparent',
                marginBottom: -1,
                background: 'transparent',
                color: activeTab === key ? 'var(--hp-gold)' : 'var(--hp-dim)',
                fontWeight: activeTab === key ? 700 : 500,
                cursor: 'pointer',
                fontSize: '0.875rem',
                transition: 'color 0.15s',
              }}
            >
              {icon}{label}
            </button>
          ))}
        </div>

        {/* DoE tab */}
        {activeTab === 'doe' && (
          <div>
            {dayState === 'holidays' && (
              <div style={{ marginBottom: 16, padding: '10px 16px', borderRadius: 'var(--radius)', background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', color: 'var(--hp-dim)', fontWeight: 600, fontSize: '0.875rem' }}>
                NSW school holidays — no DoE collections on {dayName} {selectedDate}. Depots continue as normal.
              </div>
            )}
            {dayState === 'blocked' && (
              <div style={{ marginBottom: 16, padding: '10px 16px', borderRadius: 'var(--radius)', background: 'var(--hp-red)', color: '#fff', fontWeight: 600, fontSize: '0.875rem' }}>
                DoE collections do not run on {dayName}s. Please select a Tuesday or Thursday.
              </div>
            )}
            {dayState === 'monday' && (
              <div style={{ marginBottom: 16, padding: '10px 16px', borderRadius: 'var(--radius)', border: '1px solid var(--hp-gold)', background: 'var(--hp-surface)', color: 'var(--hp-gold)', fontWeight: 600, fontSize: '0.875rem' }}>
                Monday — Inner Sydney School only.
              </div>
            )}
            {dayState !== 'blocked' && dayState !== 'holidays' && (
              <>
                <p style={{ fontSize: '0.8rem', color: 'var(--hp-dim)', marginBottom: 16 }}>
                  Enter the number of DoE parcels delivered by each driver on <strong>{dayName} {selectedDate}</strong>. Leave blank for zero.
                </p>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                    <thead>
                      <tr>
                        <th style={thStyle}>Driver Login</th>
                        <th style={thStyle}>Collection Point</th>
                        <th style={{ ...thStyle, textAlign: 'center' }}>Quantity</th>
                        <th style={thStyle}>Already entered today</th>
                      </tr>
                    </thead>
                    <tbody>
                      {doeDrivers.filter(login => isLoginAllowedOnDay(login, dayState, innerSydneyLogins)).map(login => {
                        const existing = existingDoEForDate[login];
                        const schools = liveLoginToSchools[login.toUpperCase()] || LOGIN_TO_SCHOOLS[login.toUpperCase()] || [];
                        return (
                          <tr key={login}>
                            <td style={{ ...tdStyle, fontWeight: 600 }}>{login}</td>
                            <td style={{ ...tdStyle, color: 'var(--hp-dim)', fontSize: '0.8rem' }}>
                              {schools.join(', ') || '—'}
                            </td>
                            <td style={{ ...tdStyle, textAlign: 'center' }}>
                              <input
                                type="number"
                                min="0"
                                value={doeInputs[login] ?? ''}
                                onChange={e => setDoeInputs(v => ({ ...v, [login]: e.target.value }))}
                                placeholder="—"
                                style={inputStyle}
                              />
                            </td>
                            <td style={{ ...tdStyle, color: existing != null ? 'var(--hp-green)' : 'var(--hp-dim)', fontWeight: existing != null ? 600 : 400 }}>
                              {existing != null ? `✓ ${existing}` : '—'}
                            </td>
                          </tr>
                        );
                      })}
                      {/* Relief driver row */}
                      <tr>
                        <td style={{ ...tdStyle, paddingTop: 12 }}>
                          <input
                            type="text"
                            value={doeReliefInput}
                            onChange={e => setDoeReliefInput(e.target.value.toUpperCase())}
                            onKeyDown={e => e.key === 'Enter' && addReliefDriver('doe')}
                            placeholder="Relief driver login…"
                            style={{ ...inputStyle, width: 200, textAlign: 'left', padding: '4px 10px', color: 'var(--hp-dim)' }}
                          />
                        </td>
                        <td style={tdStyle} />
                        <td style={{ ...tdStyle, paddingTop: 12, textAlign: 'center' }}>
                          <button
                            onClick={() => addReliefDriver('doe')}
                            style={{ padding: '4px 14px', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', background: 'var(--hp-surface)', color: 'var(--hp-dim)', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600 }}
                          >
                            + Add
                          </button>
                        </td>
                        <td style={tdStyle} />
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div style={{ marginTop: 20, display: 'flex', alignItems: 'center', gap: 16 }}>
                  <button
                    onClick={() => handleSave('doe')}
                    disabled={saving}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 6,
                      padding: '9px 22px', border: 'none', borderRadius: 'var(--radius)',
                      background: 'var(--hp-gold)', color: 'var(--hp-black)',
                      fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer',
                      fontSize: '0.875rem', opacity: saving ? 0.7 : 1,
                    }}
                  >
                    <Save size={14} />{saving ? 'Saving…' : 'Save DoE Entries'}
                  </button>
                  {saveResult && (
                    <span style={{ fontSize: '0.875rem', fontWeight: 600, color: saveResult.ok ? 'var(--hp-green)' : 'var(--hp-red)' }}>
                      {saveResult.msg}
                    </span>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {/* Depots tab */}
        {activeTab === 'depots' && (
          <div>
            {/* Manage who does depots — controls the portal Depot Bags task */}
            <div style={{ border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', marginBottom: 20, background: 'var(--hp-surface)' }}>
              <button
                onClick={() => setRosterOpen(o => !o)}
                style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--hp-text)', fontWeight: 700, fontSize: '0.85rem' }}
              >
                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <UserCheck size={15} color="var(--hp-gold)" /> Manage depot drivers
                  {depotRoster && (
                    <span style={{ fontWeight: 500, color: 'var(--hp-dim)', fontSize: '0.78rem' }}>
                      ({Object.values(depotRoster).filter(v => v.active).length} active)
                    </span>
                  )}
                </span>
                <span style={{ color: 'var(--hp-dim)' }}>{rosterOpen ? '▲' : '▼'}</span>
              </button>
              {rosterOpen && (
                <div style={{ padding: '0 16px 16px' }}>
                  <p style={{ fontSize: '0.78rem', color: 'var(--hp-dim)', margin: '0 0 12px' }}>
                    Tick the drivers who carry out depot collections. Only ticked drivers see the Depot Bags task in their portal — occasional drivers can log a tally of <strong>0</strong> on days they don't.
                  </p>
                  {depotRoster === null ? (
                    <div style={{ fontSize: '0.8rem', color: 'var(--hp-dim)' }}>Loading roster…</div>
                  ) : (
                    <>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(220px, 100%), 1fr))', gap: 8, maxHeight: 280, overflowY: 'auto' }}>
                        {depotRosterCandidates.map(login => {
                          const entry = depotRoster[login] || { active: false, route: '' };
                          return (
                            <div key={login} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', background: entry.active ? 'rgba(201,168,76,0.08)' : 'transparent' }}>
                              <input type="checkbox" checked={!!entry.active} onChange={() => toggleRosterActive(login)} style={{ cursor: 'pointer' }} />
                              <span style={{ fontWeight: 600, fontSize: '0.82rem', flex: 1 }}>{login}</span>
                              {entry.active && (
                                <select
                                  value={entry.route || ''}
                                  onChange={e => setRosterRoute(login, e.target.value)}
                                  style={{ ...selectStyle, padding: '2px 6px', fontSize: '0.75rem' }}
                                >
                                  <option value="">route…</option>
                                  {ROUTES.map(r => <option key={r} value={r}>{r}</option>)}
                                </select>
                              )}
                            </div>
                          );
                        })}
                      </div>
                      <div style={{ marginTop: 14, display: 'flex', alignItems: 'center', gap: 14 }}>
                        <button
                          onClick={saveDepotRoster}
                          disabled={rosterSaving}
                          style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 18px', border: 'none', borderRadius: 'var(--radius)', background: 'var(--hp-gold)', color: 'var(--hp-black)', fontWeight: 700, cursor: rosterSaving ? 'not-allowed' : 'pointer', fontSize: '0.82rem', opacity: rosterSaving ? 0.7 : 1 }}
                        >
                          <Save size={14} />{rosterSaving ? 'Saving…' : 'Save depot roster'}
                        </button>
                        {rosterResult && (
                          <span style={{ fontSize: '0.82rem', fontWeight: 600, color: rosterResult.ok ? 'var(--hp-green)' : 'var(--hp-red)' }}>{rosterResult.msg}</span>
                        )}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>

            <p style={{ fontSize: '0.8rem', color: 'var(--hp-dim)', marginBottom: 16 }}>
              Enter the depot bag tally for each driver on <strong>{selectedDate}</strong>. Leave blank for zero.
            </p>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                <thead>
                  <tr>
                    <th style={thStyle}>Driver Login</th>
                    <th style={{ ...thStyle, textAlign: 'center' }}>Tally</th>
                    <th style={thStyle}>Route</th>
                    <th style={thStyle}>Already entered today</th>
                  </tr>
                </thead>
                <tbody>
                  {depotsDrivers.map(login => {
                    const existing = existingDepotsForDate[login];
                    const route = getDepotsRoute(login);
                    return (
                      <tr key={login}>
                        <td style={{ ...tdStyle, fontWeight: 600 }}>{login}</td>
                        <td style={{ ...tdStyle, textAlign: 'center' }}>
                          <input
                            type="number"
                            min="0"
                            value={depotsInputs[login]?.tally ?? ''}
                            onChange={e => setDepotsInputs(v => ({
                              ...v,
                              [login]: { ...v[login], tally: e.target.value, route: v[login]?.route ?? (driverRouteMap[login] || '') },
                            }))}
                            placeholder="—"
                            style={inputStyle}
                          />
                        </td>
                        <td style={tdStyle}>
                          <select
                            value={route}
                            onChange={e => setDepotsInputs(v => ({ ...v, [login]: { ...v[login], route: e.target.value } }))}
                            style={selectStyle}
                          >
                            <option value="">— select —</option>
                            {ROUTES.map(r => <option key={r} value={r}>{r}</option>)}
                          </select>
                        </td>
                        <td style={{ ...tdStyle, color: existing?.length ? 'var(--hp-green)' : 'var(--hp-dim)', fontWeight: existing?.length ? 600 : 400 }}>
                          {existing?.length
                            ? existing.map((e, i) => (
                                <span key={i}>{i > 0 ? ', ' : ''}✓ {e.tally}{e.route ? ` (${e.route})` : ''}</span>
                              ))
                            : '—'}
                        </td>
                      </tr>
                    );
                  })}
                  {/* Relief driver row */}
                  <tr>
                    <td style={{ ...tdStyle, paddingTop: 12 }}>
                      <input
                        type="text"
                        value={depotsReliefInput}
                        onChange={e => setDepotsReliefInput(e.target.value.toUpperCase())}
                        onKeyDown={e => e.key === 'Enter' && addReliefDriver('depots')}
                        placeholder="Relief driver login…"
                        style={{ ...inputStyle, width: 200, textAlign: 'left', padding: '4px 10px', color: 'var(--hp-dim)' }}
                      />
                    </td>
                    <td style={{ ...tdStyle, paddingTop: 12, textAlign: 'center' }}>
                      <button
                        onClick={() => addReliefDriver('depots')}
                        style={{ padding: '4px 14px', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', background: 'var(--hp-surface)', color: 'var(--hp-dim)', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600 }}
                      >
                        + Add
                      </button>
                    </td>
                    <td style={tdStyle} />
                    <td style={tdStyle} />
                  </tr>
                </tbody>
              </table>
            </div>
            <div style={{ marginTop: 20, display: 'flex', alignItems: 'center', gap: 16 }}>
              <button
                onClick={() => handleSave('depots')}
                disabled={saving}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  padding: '9px 22px', border: 'none', borderRadius: 'var(--radius)',
                  background: 'var(--hp-gold)', color: 'var(--hp-black)',
                  fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer',
                  fontSize: '0.875rem', opacity: saving ? 0.7 : 1,
                }}
              >
                <Save size={14} />{saving ? 'Saving…' : 'Save Depots Entries'}
              </button>
              {saveResult && (
                <span style={{ fontSize: '0.875rem', fontWeight: 600, color: saveResult.ok ? 'var(--hp-green)' : 'var(--hp-red)' }}>
                  {saveResult.msg}
                </span>
              )}
            </div>
          </div>
        )}

        {/* Period tab — depot tally grid for the whole pay period */}
        {activeTab === 'period' && (
          <div>
            {!payPeriodISO ? (
              <p style={{ fontSize: '0.85rem', color: 'var(--hp-dim)' }}>Set the pay period end (top right) to load the period.</p>
            ) : (
              <>
                <p style={{ fontSize: '0.8rem', color: 'var(--hp-dim)', marginBottom: 16 }}>
                  Depot tallies for the pay period <strong>{formatDisplayDate(periodFromISO)} – {formatDisplayDate(payPeriodISO)}</strong>.
                  Click a tally to open that day in the Status tab to amend or delete it.
                  {periodLoading && <span style={{ marginLeft: 8 }}>Loading…</span>}
                </p>
                {periodError && (
                  <div style={{ marginBottom: 16, padding: '10px 16px', borderRadius: 'var(--radius)', background: 'var(--hp-red)', color: '#fff', fontWeight: 600, fontSize: '0.875rem' }}>{periodError}</div>
                )}
                {periodGrid.dupDays > 0 && (
                  <div style={{ marginBottom: 16, padding: '10px 16px', borderRadius: 'var(--radius)', border: '1px solid rgba(245,158,11,0.5)', background: 'rgba(245,158,11,0.08)', color: 'var(--hp-amber)', fontWeight: 600, fontSize: '0.85rem' }}>
                    ⚠ {periodGrid.dupDays} driver-day{periodGrid.dupDays > 1 ? 's have' : ' has'} more than one tally logged — shown in amber below. Click through and delete the wrong row.
                  </div>
                )}
                {!periodLoading && periodGrid.dates.length === 0 && !periodError && (
                  <p style={{ fontSize: '0.85rem', color: 'var(--hp-dim)' }}>No depot tallies logged in this period yet.</p>
                )}
                {periodGrid.dates.length > 0 && (
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ borderCollapse: 'collapse', fontSize: '0.85rem', whiteSpace: 'nowrap' }}>
                      <thead>
                        <tr>
                          <th style={{ ...thStyle, position: 'sticky', left: 0, background: 'var(--hp-bg)', zIndex: 1 }}>Driver</th>
                          {periodGrid.dates.map(d => (
                            <th key={d} style={{ ...thStyle, textAlign: 'center', padding: '8px 10px' }}>
                              <div>{DAY_NAMES[new Date(d + 'T00:00:00').getDay()].slice(0, 3)}</div>
                              <div style={{ fontWeight: 500 }}>{d.slice(8, 10)}/{d.slice(5, 7)}</div>
                            </th>
                          ))}
                          <th style={{ ...thStyle, textAlign: 'center', color: 'var(--hp-gold)' }}>Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {periodGrid.drivers.map(login => (
                          <tr key={login}>
                            <td style={{ ...tdStyle, fontWeight: 600, position: 'sticky', left: 0, background: 'var(--hp-bg)', zIndex: 1 }}>{login}</td>
                            {periodGrid.dates.map(d => {
                              const cell = periodGrid.cells[`${login}|${d}`];
                              if (!cell) return <td key={d} style={{ ...tdStyle, textAlign: 'center', color: 'var(--hp-dim)' }}>—</td>;
                              const dup = cell.count > 1;
                              return (
                                <td key={d} style={{ ...tdStyle, textAlign: 'center', padding: '4px 6px' }}>
                                  <button
                                    onClick={() => { setSelectedDate(d); setActiveTab('status'); setSaveResult(null); }}
                                    title={dup ? `${cell.count} separate entries on ${formatDisplayDate(d)} — click to amend` : `Click to amend ${login}'s ${formatDisplayDate(d)} entry`}
                                    style={{
                                      padding: '3px 9px', borderRadius: 'var(--radius)', cursor: 'pointer', fontWeight: 700, fontSize: '0.82rem',
                                      border: dup ? '1px solid rgba(245,158,11,0.6)' : '1px solid transparent',
                                      background: dup ? 'rgba(245,158,11,0.12)' : 'transparent',
                                      color: dup ? 'var(--hp-amber)' : 'var(--hp-text)',
                                    }}
                                  >
                                    {cell.sum}{dup ? ` (×${cell.count})` : ''}
                                  </button>
                                </td>
                              );
                            })}
                            <td style={{ ...tdStyle, textAlign: 'center', fontWeight: 700, color: 'var(--hp-gold)' }}>{periodGrid.totals[login]}</td>
                          </tr>
                        ))}
                        <tr>
                          <td style={{ ...tdStyle, fontWeight: 700, color: 'var(--hp-gold)', position: 'sticky', left: 0, background: 'var(--hp-bg)', zIndex: 1 }}>All drivers</td>
                          {periodGrid.dates.map(d => {
                            const daySum = periodGrid.drivers.reduce((s, l) => s + (periodGrid.cells[`${l}|${d}`]?.sum || 0), 0);
                            return <td key={d} style={{ ...tdStyle, textAlign: 'center', fontWeight: 600, color: 'var(--hp-dim)' }}>{daySum}</td>;
                          })}
                          <td style={{ ...tdStyle, textAlign: 'center', fontWeight: 800, color: 'var(--hp-gold)' }}>{periodGrid.grand}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Status tab — live DoE/Depot completion for the selected date */}
        {activeTab === 'status' && (
          <div>
            <p style={{ fontSize: '0.8rem', color: 'var(--hp-dim)', marginBottom: 16 }}>
              Completion for <strong>{dayName} {selectedDate}</strong>. Use the date picker above to change day.
              {statusLoading && <span style={{ marginLeft: 8 }}>Loading…</span>}
            </p>

            {/* DoE completion */}
            <div style={{ marginBottom: 28 }}>
              {(() => {
                const done = scheduledDoE.filter(s => doeStatusFor(s).state === 'collected').length;
                const missed = scheduledDoE.filter(s => doeStatusFor(s).state === 'missed').length;
                const outstanding = scheduledDoE.length - done - missed;
                return (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: 700, fontSize: '0.95rem', color: 'var(--hp-text)' }}>DoE Collections</span>
                    {scheduledDoE.length === 0
                      ? <span style={{ fontSize: '0.82rem', color: 'var(--hp-dim)' }}>No DoE scheduled on this day.</span>
                      : <>
                          <span style={{ fontSize: '0.8rem', color: 'var(--hp-green)', fontWeight: 700 }}>{done} done</span>
                          {missed > 0 && <span style={{ fontSize: '0.8rem', color: 'var(--hp-red)', fontWeight: 700 }}>{missed} couldn't collect</span>}
                          <span style={{ fontSize: '0.8rem', color: outstanding > 0 ? 'var(--hp-gold)' : 'var(--hp-dim)', fontWeight: 700 }}>{outstanding} outstanding</span>
                        </>}
                  </div>
                );
              })()}
              {scheduledDoE.length > 0 && (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                    <thead><tr>
                      <th style={thStyle}>Driver</th><th style={thStyle}>School</th><th style={thStyle}>Status</th><th style={thStyle}>Action</th>
                    </tr></thead>
                    <tbody>
                      {scheduledDoE.map(s => {
                        const st = doeStatusFor(s);
                        const label = st.state === 'collected' ? '✓ Collected'
                          : st.state === 'missed' ? `✗ Couldn't collect${st.reason ? ' — ' + st.reason : ''}`
                          : '• Outstanding';
                        const color = st.state === 'collected' ? 'var(--hp-green)' : st.state === 'missed' ? 'var(--hp-red)' : 'var(--hp-gold)';
                        const confirmed = st.state === 'collected' || st.state === 'missed';
                        const busy = doeReopenBusy === `${s.login}|${s.school}`;
                        return (
                          <tr key={s.login + s.school}>
                            <td style={{ ...tdStyle, fontWeight: 600 }}>
                              {s.login}
                              {s.relief && <span style={{ marginLeft: 6, fontSize: '0.7rem', fontWeight: 700, color: 'var(--hp-gold)' }}>RELIEF</span>}
                            </td>
                            <td style={tdStyle}>{s.school}</td>
                            <td style={{ ...tdStyle, color, fontWeight: 600 }}>{label}</td>
                            <td style={tdStyle}>
                              {confirmed ? (
                                <button
                                  onClick={() => reopenDoe(s.login, s.school)}
                                  disabled={busy}
                                  title={`Reopen ${s.login}'s ${s.school} collection — returns the QR + Collected button in their app`}
                                  style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 9px', borderRadius: 'var(--radius)', border: '1px solid var(--hp-gold)', background: 'var(--hp-surface)', color: 'var(--hp-gold)', fontWeight: 700, fontSize: '0.72rem', cursor: busy ? 'not-allowed' : 'pointer', opacity: busy ? 0.6 : 1 }}
                                >
                                  <RotateCcw size={11} /> {busy ? '…' : 'Reopen'}
                                </button>
                              ) : <span style={{ color: 'var(--hp-dim)' }}>—</span>}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Depot submissions */}
            <div>
              <div style={{ fontWeight: 700, fontSize: '0.95rem', color: 'var(--hp-text)', marginBottom: 12 }}>Depot Tallies</div>
              {(() => {
                const activeLogins = Object.entries(depotRoster || {}).filter(([, v]) => v.active).map(([l]) => l);
                if (!activeLogins.length) return <div style={{ fontSize: '0.82rem', color: 'var(--hp-dim)' }}>No active depot drivers on the roster.</div>;
                return (
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                      <thead><tr>
                        <th style={thStyle}>Driver</th><th style={thStyle}>Tally</th><th style={thStyle}>Route</th><th style={thStyle}>Status</th>
                      </tr></thead>
                      <tbody>
                        {activeLogins.sort().map(login => {
                          const row = depotsByDate.find(r => r.login === login);
                          return (
                            <tr key={login}>
                              <td style={{ ...tdStyle, fontWeight: 600 }}>{login}</td>
                              <td style={tdStyle}>{row ? row.tally : '—'}</td>
                              <td style={tdStyle}>{row ? (row.route || '') : ''}</td>
                              <td style={{ ...tdStyle, color: row ? 'var(--hp-green)' : 'var(--hp-gold)', fontWeight: 600 }}>
                                {row ? (row.tally === 0 ? '✓ Submitted (0)' : '✓ Submitted') : '• Outstanding'}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                );
              })()}

              {/* Amend logged rows — fix a wrong tally, reassign to the correct driver, or delete */}
              <div style={{ marginTop: 16 }}>
                <div style={{ fontWeight: 700, fontSize: '0.82rem', color: 'var(--hp-dim)', marginBottom: 8 }}>
                  Logged rows for this day — amend or delete
                </div>
                {depotsByDate.length === 0 ? (
                  <div style={{ fontSize: '0.82rem', color: 'var(--hp-dim)' }}>No depot rows logged for this day.</div>
                ) : (
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                      <thead><tr>
                        <th style={thStyle}>Driver</th><th style={thStyle}>Tally</th><th style={thStyle}>Route</th><th style={thStyle}>Actions</th>
                      </tr></thead>
                      <tbody>
                        {depotsByDate.map(r => {
                          const editing = depotEdit && depotEdit.id === r.id;
                          const inputStyle = { padding: '3px 6px', borderRadius: 'var(--radius)', border: '1px solid var(--hp-gold)', background: 'var(--hp-bg)', color: 'var(--hp-text)' };
                          return (
                            <tr key={r.id}>
                              <td style={{ ...tdStyle, fontWeight: 600 }}>
                                {editing ? (
                                  <select value={depotEdit.driver} onChange={e => setDepotEdit(d => ({ ...d, driver: e.target.value }))} style={inputStyle}>
                                    {!depotDriverOptions.includes(depotEdit.driver) && <option value={depotEdit.driver}>{depotEdit.driver}</option>}
                                    {depotDriverOptions.map(l => <option key={l} value={l}>{l}</option>)}
                                  </select>
                                ) : r.login}
                              </td>
                              <td style={tdStyle}>
                                {editing ? (
                                  <input type="number" min="0" value={depotEdit.tally}
                                    onChange={e => setDepotEdit(d => ({ ...d, tally: e.target.value }))}
                                    onKeyDown={e => { if (e.key === 'Enter') saveDepotEdit(); if (e.key === 'Escape') setDepotEdit(null); }}
                                    style={{ ...inputStyle, width: 64 }} />
                                ) : r.tally}
                              </td>
                              <td style={tdStyle}>
                                {editing ? (
                                  <select value={depotEdit.route} onChange={e => setDepotEdit(d => ({ ...d, route: e.target.value }))} style={inputStyle}>
                                    <option value="">—</option>
                                    {!DEPOT_ROUTE_OPTIONS.includes(depotEdit.route) && depotEdit.route && <option value={depotEdit.route}>{depotEdit.route}</option>}
                                    {DEPOT_ROUTE_OPTIONS.map(rt => <option key={rt} value={rt}>{rt}</option>)}
                                  </select>
                                ) : (r.route || '')}
                              </td>
                              <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>
                                {editing ? (
                                  <>
                                    <button onClick={saveDepotEdit} disabled={depotRowBusy}
                                      style={{ background: 'none', border: 'none', color: 'var(--hp-green)', fontWeight: 800, cursor: 'pointer' }}>{depotRowBusy ? '…' : '✓ Save'}</button>
                                    <button onClick={() => { setDepotEdit(null); setDepotRowMsg(''); }} disabled={depotRowBusy}
                                      style={{ background: 'none', border: 'none', color: 'var(--hp-dim)', fontWeight: 700, cursor: 'pointer', marginLeft: 8 }}>✕ Cancel</button>
                                  </>
                                ) : (
                                  <>
                                    <button onClick={() => { setDepotEdit({ id: r.id, driver: r.login, tally: String(r.tally), route: r.route || '' }); setDepotRowMsg(''); }}
                                      style={{ background: 'none', border: 'none', color: 'var(--hp-gold)', fontWeight: 700, cursor: 'pointer' }}>✎ Edit</button>
                                    <button onClick={() => { if (window.confirm(`Delete this depot row (${r.login} · ${r.tally} bags)? This cannot be undone.`)) removeDepotRow(r.id); }} disabled={depotRowBusy}
                                      style={{ background: 'none', border: 'none', color: 'var(--hp-red)', fontWeight: 700, cursor: 'pointer', marginLeft: 10 }}>🗑 Delete</button>
                                  </>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    {depotRowMsg && <div style={{ color: 'var(--hp-red)', fontSize: '0.78rem', marginTop: 6 }}>{depotRowMsg}</div>}
                  </div>
                )}
              </div>
            </div>

            {/* Carry-over parcels */}
            <div style={{ marginTop: 28 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
                <span style={{ fontWeight: 700, fontSize: '0.95rem', color: 'var(--hp-text)' }}>Carry-Over Parcels</span>
                {carryByDate.length === 0
                  ? <span style={{ fontSize: '0.82rem', color: 'var(--hp-dim)' }}>No carry-over reports for this day yet.</span>
                  : <>
                      <span style={{ fontSize: '0.8rem', color: carrySplit.totalParcels > 0 ? 'var(--hp-amber)' : 'var(--hp-dim)', fontWeight: 700 }}>
                        {carrySplit.totalParcels} parcel{carrySplit.totalParcels === 1 ? '' : 's'} carried over
                      </span>
                      <span style={{ fontSize: '0.8rem', color: 'var(--hp-green)', fontWeight: 700 }}>
                        {carrySplit.clear.length} all clear
                      </span>
                    </>}
              </div>
              {Object.keys(carrySplit.byReason).length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
                  {Object.entries(carrySplit.byReason).map(([reason, count]) => (
                    <span key={reason} style={{ fontSize: '0.75rem', fontWeight: 600, padding: '4px 10px', borderRadius: 999, background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.35)', color: 'var(--hp-amber)' }}>
                      {reason}: {count}
                    </span>
                  ))}
                </div>
              )}
              {carrySplit.carried.length > 0 && (
                <div style={{ overflowX: 'auto', marginBottom: 12 }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                    <thead><tr>
                      <th style={thStyle}>Driver</th><th style={thStyle}>Reason</th><th style={thStyle}>Parcels</th><th style={thStyle}>Note</th><th style={thStyle}>Photo</th>
                    </tr></thead>
                    <tbody>
                      {carrySplit.carried.map((r, i) => {
                        const isEditing = carryRowEdit && carryRowEdit.driver === r.driver && carryRowEdit.reason === r.reason;
                        return (
                          <tr key={r.driver + r.reason + i}>
                            <td style={{ ...tdStyle, fontWeight: 600 }}>{r.driver}</td>
                            <td style={{ ...tdStyle, color: (r.reason || '').startsWith('Incorrect') ? 'var(--hp-amber)' : 'var(--hp-text)' }}>{r.reason}</td>
                            <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>
                              {isEditing ? (
                                <>
                                  <input type="number" min="0" autoFocus value={carryRowEdit.value}
                                    onChange={e => setCarryRowEdit(re => ({ ...re, value: e.target.value }))}
                                    onKeyDown={e => { if (e.key === 'Enter') saveCarryRowCount(); if (e.key === 'Escape') setCarryRowEdit(null); }}
                                    style={{ width: 56, padding: '3px 6px', borderRadius: 'var(--radius)', border: '1px solid var(--hp-gold)', background: 'var(--hp-bg)', color: 'var(--hp-text)' }} />
                                  <button onClick={saveCarryRowCount} disabled={carryRowBusy}
                                    style={{ background: 'none', border: 'none', color: 'var(--hp-green)', fontWeight: 800, cursor: 'pointer' }}>{carryRowBusy ? '…' : '✓'}</button>
                                  <button onClick={() => setCarryRowEdit(null)}
                                    style={{ background: 'none', border: 'none', color: 'var(--hp-dim)', fontWeight: 700, cursor: 'pointer' }}>✕</button>
                                </>
                              ) : (
                                <>
                                  {r.count}
                                  <button
                                    onClick={() => setCarryRowEdit({ driver: r.driver, reason: r.reason, value: String(r.count) })}
                                    title="Correct this count before sending (0 removes the row)"
                                    style={{ background: 'none', border: 'none', color: 'var(--hp-gold)', fontWeight: 700, fontSize: '0.75rem', cursor: 'pointer', marginLeft: 6 }}
                                  >✎</button>
                                </>
                              )}
                            </td>
                            <td style={tdStyle}>{r.note || ''}</td>
                            <td style={tdStyle}>
                              {r.photo
                                ? r.photo.split('\n').filter(Boolean).map((u, i, a) => (
                                    <span key={i}>{i > 0 ? ' · ' : ''}<a href={u} target="_blank" rel="noreferrer" style={{ color: 'var(--hp-gold)', fontWeight: 600 }}>{a.length > 1 ? `Photo ${i + 1}` : 'View photo'}</a></span>
                                  ))
                                : ''}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
              {carrySplit.clear.length > 0 && (
                <div style={{ fontSize: '0.8rem', color: 'var(--hp-dim)' }}>
                  <strong style={{ color: 'var(--hp-green)' }}>All delivered/transferred:</strong>{' '}
                  {carrySplit.clear.join(', ')}
                </div>
              )}

              <CarryOverIncidents date={selectedDate} drivers={carryDrivers} nameOf={carryNameOf} onChange={setCarryIncidents} />

              {/* Email to Australia Post */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginTop: 14, padding: '12px 14px', borderRadius: 'var(--radius)', border: '1px solid var(--hp-border)', background: 'var(--hp-surface)' }}>
                <button
                  onClick={sendCarryOverEmail}
                  disabled={carrySendBusy || !carryEmail?.to}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8, padding: '9px 18px', border: 'none', borderRadius: 'var(--radius)',
                    background: !carryEmail?.to ? 'var(--hp-border)' : carrySendConfirm ? 'var(--hp-red)' : 'var(--hp-gold)',
                    color: !carryEmail?.to ? 'var(--hp-dim)' : carrySendConfirm ? '#fff' : 'var(--hp-black)',
                    fontWeight: 700, fontSize: '0.85rem', cursor: carrySendBusy || !carryEmail?.to ? 'not-allowed' : 'pointer',
                  }}
                >
                  <Send size={14} />
                  {carrySendBusy ? 'Sending…' : carrySendConfirm ? 'Click again to confirm' : carryEmail?.sent ? 'Send again to Australia Post' : 'Email report to Australia Post'}
                </button>
                <span style={{ fontSize: '0.78rem', color: 'var(--hp-dim)' }}>
                  {carryEmail?.sent
                    ? <span style={{ color: 'var(--hp-green)', fontWeight: 700 }}>✓ Sent {new Date(carryEmail.sent.sentAt).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' })} by {carryEmail.sent.by}</span>
                    : 'Not sent for this date yet.'}
                  {carryEmail?.to ? <> · To: <strong style={{ color: 'var(--hp-text)' }}>{carryEmail.to}</strong></> : ' · No recipient set.'}
                  {carryEmail?.cc ? <> · CC: {carryEmail.cc}</> : ''}
                  {' · Auto-send: '}<strong style={{ color: carryEmail?.autoSend ? 'var(--hp-green)' : 'var(--hp-dim)' }}>{carryEmail?.autoSend ? 'ON' : 'OFF'}</strong>
                </span>
                {!carryCfgEdit && (
                  <button
                    onClick={() => setCarryCfgEdit({ to: carryEmail?.to || '', cc: carryEmail?.cc || '', autoSend: !!carryEmail?.autoSend })}
                    style={{ background: 'none', border: 'none', color: 'var(--hp-gold)', fontWeight: 700, fontSize: '0.78rem', cursor: 'pointer' }}
                  >✎ Edit settings</button>
                )}
                {carrySendResult && (
                  <span style={{ fontSize: '0.78rem', fontWeight: 700, color: carrySendResult.ok ? 'var(--hp-green)' : 'var(--hp-red)' }}>
                    {carrySendResult.ok ? '✓ ' : '✗ '}{carrySendResult.msg}
                  </span>
                )}
                {carryCfgEdit && (
                  <div style={{ width: '100%', display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', marginTop: 4 }}>
                    <input type="email" placeholder="To (Australia Post)" value={carryCfgEdit.to}
                      onChange={e => setCarryCfgEdit(c => ({ ...c, to: e.target.value }))}
                      style={{ padding: '7px 10px', borderRadius: 'var(--radius)', border: '1px solid var(--hp-border)', background: 'var(--hp-bg)', color: 'var(--hp-text)', fontSize: '0.8rem', width: 230 }} />
                    <input type="email" placeholder="CC (optional)" value={carryCfgEdit.cc}
                      onChange={e => setCarryCfgEdit(c => ({ ...c, cc: e.target.value }))}
                      style={{ padding: '7px 10px', borderRadius: 'var(--radius)', border: '1px solid var(--hp-border)', background: 'var(--hp-bg)', color: 'var(--hp-text)', fontSize: '0.8rem', width: 230 }} />
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.78rem', color: 'var(--hp-text)', fontWeight: 600, cursor: 'pointer' }}>
                      <input type="checkbox" checked={carryCfgEdit.autoSend} onChange={e => setCarryCfgEdit(c => ({ ...c, autoSend: e.target.checked }))} />
                      Auto-send when all drivers finished
                    </label>
                    <button onClick={saveCarryCfg} disabled={carryCfgBusy}
                      style={{ padding: '7px 14px', background: 'var(--hp-gold)', border: 'none', borderRadius: 'var(--radius)', color: 'var(--hp-black)', fontWeight: 700, fontSize: '0.78rem', cursor: 'pointer' }}>
                      {carryCfgBusy ? 'Saving…' : '✓ Save'}
                    </button>
                    <button onClick={() => setCarryCfgEdit(null)}
                      style={{ padding: '7px 14px', background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', color: 'var(--hp-dim)', fontWeight: 700, fontSize: '0.78rem', cursor: 'pointer' }}>
                      Cancel
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* QR Codes tab */}
        {activeTab === 'qr' && (
          <div>
            {/* Permanent driver change */}
            <div style={{ marginBottom: 28, padding: '16px', borderRadius: 'var(--radius)', border: '1px solid var(--hp-border)', background: 'var(--hp-surface)' }}>
              <span style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10 }}>
                Permanent driver change — updates every school the driver is assigned to, app-wide
              </span>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
                <select
                  value={swapFrom}
                  onChange={e => { setSwapFrom(e.target.value); setSwapConfirm(false); setSwapResult(null); }}
                  style={{ padding: '8px 10px', borderRadius: 'var(--radius)', border: '1px solid var(--hp-border)', background: 'var(--hp-bg)', color: 'var(--hp-text)', fontSize: '0.85rem', cursor: 'pointer' }}
                >
                  <option value="">— select driver —</option>
                  {doeAssignedLogins.map(l => <option key={l} value={l}>{l}</option>)}
                </select>
                <select
                  value={swapMode}
                  onChange={e => { setSwapMode(e.target.value); setSwapConfirm(false); setSwapResult(null); }}
                  style={{ padding: '8px 10px', borderRadius: 'var(--radius)', border: '1px solid var(--hp-border)', background: 'var(--hp-bg)', color: 'var(--hp-text)', fontSize: '0.85rem', cursor: 'pointer' }}
                >
                  <option value="replace">replace with…</option>
                  <option value="remove">remove permanently (left the business)</option>
                </select>
                {swapMode === 'replace' && (
                  <>
                    <input
                      type="text" list="swap-to-logins" placeholder="New driver login…"
                      value={swapTo}
                      onChange={e => { setSwapTo(e.target.value.toUpperCase()); setSwapConfirm(false); setSwapResult(null); }}
                      style={{ padding: '8px 10px', borderRadius: 'var(--radius)', border: '1px solid var(--hp-border)', background: 'var(--hp-bg)', color: 'var(--hp-text)', fontSize: '0.85rem', width: 180 }}
                    />
                    <datalist id="swap-to-logins">
                      {qrAllLogins.map(l => <option key={l} value={l} />)}
                    </datalist>
                  </>
                )}
                <button
                  onClick={applyPermanentSwap}
                  disabled={!swapReady || swapBusy}
                  style={{
                    padding: '8px 18px', border: 'none', borderRadius: 'var(--radius)',
                    background: !swapReady ? 'var(--hp-border)' : swapConfirm ? 'var(--hp-red)' : 'var(--hp-gold)',
                    color: !swapReady ? 'var(--hp-dim)' : swapConfirm ? '#fff' : 'var(--hp-black)',
                    fontWeight: 700, fontSize: '0.85rem', cursor: swapReady && !swapBusy ? 'pointer' : 'not-allowed',
                  }}
                >
                  {swapBusy ? 'Applying…' : swapConfirm ? 'Tap again to confirm' : 'Apply change'}
                </button>
              </div>
              {swapFrom && swapAffectedSchools.length > 0 && (
                <div style={{ fontSize: '0.78rem', color: 'var(--hp-dim)', marginTop: 10 }}>
                  Affects: <strong style={{ color: 'var(--hp-text)' }}>{swapAffectedSchools.join(', ')}</strong>
                  {swapMode === 'remove' && swapAffectedSchools.length > 0 && (
                    <span style={{ color: 'var(--hp-red)', fontWeight: 600 }}> — these schools will have no assigned driver until you set one.</span>
                  )}
                </div>
              )}
              {swapResult && (
                <div style={{ fontSize: '0.8rem', fontWeight: 600, marginTop: 10, color: swapResult.ok ? 'var(--hp-green)' : 'var(--hp-red)' }}>
                  {swapResult.ok ? '✓ ' : '✗ '}{swapResult.msg}
                </div>
              )}
            </div>

            {/* Bulk alert buttons */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 28, padding: '16px', borderRadius: 'var(--radius)', border: '1px solid var(--hp-border)', background: 'var(--hp-surface)' }}>
              <span style={{ width: '100%', fontSize: '0.75rem', fontWeight: 600, color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>
                Bulk Alerts — alert all providers for a day
              </span>
              {Object.entries(QR_BULK_DAYS).map(([dayKey, { label, points }]) => {
                const bs = qrBulkStatus[dayKey];
                const isSending = bs?.sending;
                return (
                  <div key={dayKey} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <button
                      onClick={() => sendQrBulkAlerts(dayKey)}
                      disabled={isSending}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 8,
                        padding: '9px 20px', border: '2px solid var(--hp-gold)', borderRadius: 'var(--radius)',
                        background: isSending ? 'var(--hp-surface)' : 'var(--hp-gold)',
                        color: isSending ? 'var(--hp-gold)' : 'var(--hp-black)',
                        fontWeight: 700, fontSize: '0.875rem',
                        cursor: isSending ? 'not-allowed' : 'pointer',
                        opacity: isSending ? 0.8 : 1, whiteSpace: 'nowrap',
                      }}
                    >
                      <Send size={14} />
                      {isSending ? `Sending… (${bs.sent + bs.failed}/${bs.total})` : label}
                    </button>
                    {bs && !bs.sending && (
                      <div style={{ fontSize: '0.75rem', fontWeight: 600, paddingLeft: 4, color: bs.failed === 0 ? 'var(--hp-green)' : 'var(--hp-red)' }}>
                        {bs.failed === 0 ? `✓ All ${bs.sent} sent` : `✓ ${bs.sent} sent  ✗ ${bs.failed} failed`}
                      </div>
                    )}
                    {!bs && <div style={{ fontSize: '0.72rem', color: 'var(--hp-dim)', paddingLeft: 4 }}>{points.length} provider{points.length !== 1 ? 's' : ''}</div>}
                  </div>
                );
              })}
            </div>

            {/* Cards grid */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(260px, 100%), 1fr))', gap: 16 }}>
              {livePoints.map(cp => {
                const activeLogin = getActiveQrDriver(cp);
                const cover = coverOnDate(cp);
                const upcoming = cover || nextCover(cp);
                const draft = qrCoverDraft[cp.qr] || { period: '1w' };
                const status = qrSmsStatus[cp.qr];
                const hasPhone = !!phoneByLogin[activeLogin.toUpperCase()];
                const cardStyle = { background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', padding: '16px', display: 'flex', flexDirection: 'column', gap: 12 };
                const labelStyle = { fontSize: '0.7rem', fontWeight: 600, color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.05em' };
                return (
                  <div key={cp.qr} style={cardStyle}>
                    <div style={{ fontWeight: 700, fontSize: '0.95rem', color: 'var(--hp-text)', lineHeight: 1.3 }}>{cp.school}</div>
                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                      <img src={qrImageUrl(cp.qr)} alt={`QR for ${cp.school}`} width={160} height={160} style={{ borderRadius: 6, border: '1px solid var(--hp-border)' }} />
                    </div>
                    <div style={{ textAlign: 'center', fontSize: '0.7rem', color: 'var(--hp-dim)', fontFamily: 'monospace' }}>{cp.qr}</div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                      <div>
                        <div style={labelStyle}>Usual driver{cp.drivers.length > 1 ? 's' : ''}</div>
                        {schoolEdit === cp.school ? (
                          <div style={{ marginTop: 4 }}>
                            <input
                              type="text" autoFocus
                              value={schoolEditValue}
                              onChange={e => setSchoolEditValue(e.target.value.toUpperCase())}
                              onKeyDown={e => { if (e.key === 'Enter') saveSchoolDrivers(cp.school); if (e.key === 'Escape') { setSchoolEdit(null); setSchoolEditError(''); } }}
                              placeholder="LOGIN1, LOGIN2"
                              style={{ width: '100%', padding: '5px 8px', borderRadius: 'var(--radius)', border: '1px solid var(--hp-gold)', background: 'var(--hp-bg)', color: 'var(--hp-text)', fontSize: '0.8rem' }}
                            />
                            <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                              <button onClick={() => saveSchoolDrivers(cp.school)} disabled={schoolEditBusy}
                                style={{ background: 'none', border: 'none', color: 'var(--hp-green)', fontWeight: 700, fontSize: '0.75rem', cursor: 'pointer', padding: 0 }}>
                                {schoolEditBusy ? 'Saving…' : '✓ Save'}
                              </button>
                              <button onClick={() => { setSchoolEdit(null); setSchoolEditError(''); }}
                                style={{ background: 'none', border: 'none', color: 'var(--hp-dim)', fontWeight: 600, fontSize: '0.75rem', cursor: 'pointer', padding: 0 }}>
                                Cancel
                              </button>
                            </div>
                            {schoolEditError && <div style={{ fontSize: '0.72rem', color: 'var(--hp-red)', marginTop: 2 }}>{schoolEditError}</div>}
                          </div>
                        ) : (
                          <div style={{ fontSize: '0.875rem', fontWeight: 600, color: cp.drivers.length ? 'var(--hp-text)' : 'var(--hp-red)', marginTop: 2 }}>
                            {cp.drivers.length ? cp.drivers.join(' / ') : 'No driver assigned'}
                            <button
                              onClick={() => { setSchoolEdit(cp.school); setSchoolEditValue(cp.drivers.join(', ')); setSchoolEditError(''); }}
                              title="Edit assigned drivers permanently"
                              style={{ background: 'none', border: 'none', color: 'var(--hp-gold)', fontWeight: 700, fontSize: '0.75rem', cursor: 'pointer', marginLeft: 6, padding: 0 }}
                            >✎ edit</button>
                          </div>
                        )}
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={labelStyle}>Collections</div>
                        <div style={{ fontSize: '1.1rem', fontWeight: 700, color: 'var(--hp-gold)', marginTop: 2 }}>1</div>
                      </div>
                    </div>
                    <div>
                      <div style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: 4, marginBottom: 4 }}><UserCheck size={11} /> Assign a driver</div>
                      {upcoming && (
                        <div style={{ fontSize: '0.75rem', padding: '6px 8px', borderRadius: 'var(--radius)', background: 'rgba(212,175,55,0.10)', border: '1px solid var(--hp-gold)', marginBottom: 6, display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'flex-start' }}>
                          <span style={{ color: 'var(--hp-text)' }}>
                            <strong>{upcoming.relief}</strong> {cover ? 'covering' : 'will cover'} {upcoming.from === upcoming.to ? shortDay(upcoming.from) : `${shortDay(upcoming.from)} – ${shortDay(upcoming.to)}`}
                            <span style={{ color: 'var(--hp-dim)' }}> · {upcoming.dates.length} collection{upcoming.dates.length === 1 ? '' : 's'}</span>
                          </span>
                          <button onClick={() => endCover(cp, upcoming)} title="Hand the remaining dates back to the usual driver"
                            style={{ background: 'none', border: 'none', color: 'var(--hp-red)', fontWeight: 700, fontSize: '0.72rem', cursor: 'pointer', padding: 0, whiteSpace: 'nowrap' }}>End</button>
                        </div>
                      )}
                      <div style={{ display: 'flex', gap: 6 }}>
                        <select
                          value={draft.driver || ''}
                          onChange={e => updateDraft(cp, { driver: e.target.value })}
                          style={{ flex: 1, minWidth: 0, padding: '5px 8px', borderRadius: 'var(--radius)', border: '1px solid var(--hp-border)', background: 'var(--hp-bg)', color: 'var(--hp-text)', fontSize: '0.8rem', cursor: 'pointer' }}
                        >
                          <option value="">— driver —</option>
                          {qrAllLogins.filter(l => !cp.drivers.includes(l)).map(l => <option key={l} value={l}>{l}</option>)}
                        </select>
                        <select
                          value={draft.period || '1w'}
                          onChange={e => updateDraft(cp, { period: e.target.value })}
                          style={{ width: 118, padding: '5px 8px', borderRadius: 'var(--radius)', border: '1px solid var(--hp-border)', background: 'var(--hp-bg)', color: 'var(--hp-text)', fontSize: '0.8rem', cursor: 'pointer' }}
                        >
                          {COVER_PERIOD_OPTIONS.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
                        </select>
                      </div>
                      {draft.driver && (
                        <>
                          <div style={{ fontSize: '0.72rem', color: 'var(--hp-dim)', marginTop: 5, lineHeight: 1.4 }}>
                            {draft.period === 'permanent'
                              ? <>Makes <strong style={{ color: 'var(--hp-text)' }}>{draft.driver}</strong> the usual driver in place of {cp.drivers[0] || '—'} from {shortDay(selectedDate)}.</>
                              : draft.dates == null ? 'Checking dates…'
                              : draft.dates.length === 0 ? <span style={{ color: 'var(--hp-red)' }}>No collections in that window (school holidays).</span>
                              : <>From {shortDay(selectedDate)}: {draft.dates.map(shortDay).join(', ')}</>}
                          </div>
                          <button
                            onClick={() => assignCover(cp)}
                            disabled={draft.busy || (draft.period !== 'permanent' && !(draft.dates && draft.dates.length))}
                            style={{
                              width: '100%', marginTop: 6, padding: '7px 12px', borderRadius: 'var(--radius)', fontWeight: 700, fontSize: '0.8rem', cursor: 'pointer',
                              border: '1px solid var(--hp-gold)', background: draft.confirm ? 'var(--hp-gold)' : 'transparent', color: draft.confirm ? 'var(--hp-black)' : 'var(--hp-gold)',
                              opacity: draft.busy ? 0.7 : 1,
                            }}
                          >
                            {draft.busy ? 'Assigning…' : draft.confirm ? `Confirm — assign & text ${draft.driver} once` : `Assign ${draft.driver} · ${COVER_PERIOD_OPTIONS.find(o => o[0] === draft.period)[1].toLowerCase()}`}
                          </button>
                        </>
                      )}
                      {draft.result && (
                        <div style={{ fontSize: '0.72rem', fontWeight: 600, marginTop: 5, color: draft.result.ok ? 'var(--hp-green)' : 'var(--hp-red)' }}>
                          {draft.result.ok ? '✓ ' : '✗ '}{draft.result.msg}
                        </div>
                      )}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      <button
                        onClick={() => sendQrSMS(cp)}
                        disabled={status?.sending}
                        style={{
                          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                          padding: '7px 12px', border: 'none', borderRadius: 'var(--radius)',
                          background: hasPhone ? 'var(--hp-gold)' : 'var(--hp-border)',
                          color: hasPhone ? 'var(--hp-black)' : 'var(--hp-dim)',
                          fontWeight: 700, fontSize: '0.8rem',
                          cursor: status?.sending ? 'not-allowed' : 'pointer',
                          opacity: status?.sending ? 0.7 : 1,
                        }}
                      >
                        <MessageSquare size={13} />
                        {status?.sending ? 'Sending…' : `Alert ${activeLogin}`}
                      </button>
                      {status?.result && (
                        <div style={{ fontSize: '0.75rem', fontWeight: 600, color: status.result.ok ? 'var(--hp-green)' : 'var(--hp-red)', textAlign: 'center' }}>
                          {status.result.ok ? '✓ ' : '✗ '}{status.result.msg}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
